(() => {
  'use strict';

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let lastTrigger = null;

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency: window.GoldCaftane?.currency || 'MAD',
    maximumFractionDigits: 2
  }).format((Number(cents) || 0) / 100);

  function openDialog(id, trigger) {
    const dialog = document.getElementById(id);
    if (!dialog) return;
    lastTrigger = trigger || document.activeElement;
    if (!dialog.open) {
      try {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      } catch (error) {
        dialog.setAttribute('open', '');
      }
    }
    const target = $(focusableSelector, dialog);
    target?.focus();
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    if (lastTrigger instanceof HTMLElement) lastTrigger.focus();
  }

  function trapFocus(event, root) {
    if (event.key !== 'Tab') return;
    const focusable = $$(focusableSelector, root).filter((element) => !element.hasAttribute('hidden'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function initAnnouncement() {
    const bar = $('[data-announcement-bar]');
    if (!bar) return;
    const storageKey = 'gold-caftane-announcement-dismissed';
    if (sessionStorage.getItem(storageKey) === 'true') {
      bar.classList.add('is-hidden');
      return;
    }
    const messages = $$('[data-announcement-item]', bar);
    if (messages.length > 1) {
      let active = 0;
      window.setInterval(() => {
        messages[active].classList.remove('is-active');
        active = (active + 1) % messages.length;
        messages[active].classList.add('is-active');
      }, 4200);
    }
    $('[data-dismiss-announcement]', bar)?.addEventListener('click', () => {
      bar.classList.add('is-hidden');
      sessionStorage.setItem(storageKey, 'true');
    });
  }

  function initHeader() {
    const header = $('[data-site-header]');
    if (!header?.classList.contains('site-header--overlay')) return;
    const setHeaderState = () => header.classList.toggle('is-solid', window.scrollY > 60);
    setHeaderState();
    window.addEventListener('scroll', setHeaderState, { passive: true });
  }

  function initServiceSlider() {
    $$('[data-service-slider]').forEach((slider) => {
      const track = $('[data-service-track]', slider);
      const dots = $$('[data-service-dot]', slider);
      if (!track || !dots.length) return;
      const mobile = window.matchMedia('(max-width: 999px)');
      let active = 0;
      let interval;

      const show = (index) => {
        active = (index + dots.length) % dots.length;
        track.style.transform = mobile.matches ? `translateX(-${active * 25}%)` : '';
        dots.forEach((dot, dotIndex) => {
          const selected = dotIndex === active;
          dot.classList.toggle('is-active', selected);
          dot.setAttribute('aria-current', selected ? 'true' : 'false');
        });
      };

      const start = () => {
        window.clearInterval(interval);
        if (mobile.matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          interval = window.setInterval(() => show(active + 1), 4200);
        }
      };

      dots.forEach((dot, index) => dot.addEventListener('click', () => {
        show(index);
        start();
      }));
      slider.addEventListener('mouseenter', () => window.clearInterval(interval));
      slider.addEventListener('mouseleave', start);
      mobile.addEventListener('change', () => {
        show(active);
        start();
      });
      show(0);
      start();
    });
  }

  function initLocalization() {
    $$('[data-localization-select]').forEach((select) => {
      select.addEventListener('change', () => select.form?.requestSubmit());
    });
  }

  function initGallery() {
    $$('[data-product-gallery]').forEach((gallery) => {
      const slides = $$('[data-gallery-slide]', gallery);
      const viewport = $('[data-gallery-main]', gallery);
      const activate = (mediaId) => {
        const activeSlide = slides.find((item) => item.dataset.gallerySlide === String(mediaId) && !item.hidden) || slides.find((item) => !item.hidden);
        if (!activeSlide) return;
        slides.forEach((item) => item.classList.toggle('is-active', item === activeSlide));
        viewport?.scrollTo({ left: activeSlide.offsetLeft, behavior: 'smooth' });
      };

      gallery.addEventListener('gold:filter-gallery', (event) => {
        const { color, mediaId } = event.detail || {};
        const normalizedColor = String(color || '').trim().toLowerCase();
        const matchingSlides = normalizedColor ? slides.filter((item) => item.dataset.galleryColors?.includes(normalizedColor)) : [];
        const selectedMedia = slides.find((item) => item.dataset.gallerySlide === String(mediaId));
        // A matching colour name (from alt text or the uploaded filename) can have several photos.
        // If a merchant has not named the media to match the option yet, keep the variant's assigned
        // Shopify image as a safe fallback rather than accidentally displaying another colour.
        const visibleSlides = normalizedColor
          ? (matchingSlides.length ? matchingSlides : (selectedMedia ? [selectedMedia] : slides))
          : slides;
        slides.forEach((item) => { item.hidden = !visibleSlides.includes(item); });
        const preferred = visibleSlides.some((item) => item.dataset.gallerySlide === String(mediaId))
          ? mediaId
          : visibleSlides[0]?.dataset.gallerySlide;
        activate(preferred);
      });

      let scrollFrame;
      viewport?.addEventListener('scroll', () => {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(() => {
          const visibleSlides = slides.filter((slide) => !slide.hidden);
          const activeSlide = visibleSlides.reduce((closest, slide) => {
            return Math.abs(slide.offsetLeft - viewport.scrollLeft) < Math.abs(closest.offsetLeft - viewport.scrollLeft) ? slide : closest;
          }, visibleSlides[0]);
          if (activeSlide) slides.forEach((slide) => slide.classList.toggle('is-active', slide === activeSlide));
        });
      }, { passive: true });

      $('[data-open-product-zoom]', gallery)?.addEventListener('click', (event) => {
        const dialogId = event.currentTarget.dataset.productZoomDialog;
        const dialog = document.getElementById(dialogId);
        const activeImage = $('.product-gallery__slide.is-active img', gallery);
        const zoomImage = $('[data-product-zoom-image]', dialog);
        if (activeImage && zoomImage) {
          zoomImage.src = activeImage.currentSrc || activeImage.src;
          zoomImage.alt = activeImage.alt;
        }
        openDialog(dialogId, event.currentTarget);
      });
    });
  }

  function initVariantPickers() {
    $$('[data-variant-picker]').forEach((picker) => {
      const form = picker.closest('form');
      const data = $('[data-variant-json]', picker)?.textContent;
      if (!form || !data) return;
      let variants;
      try { variants = JSON.parse(data); } catch (error) { return; }

      const selectedId = $('[data-selected-variant]', form);
      const submit = $('[data-product-submit]', form);
      const status = $('.product-form__status', form);
      const scope = picker.closest('.product-information');
      const priceWrapper = scope ? $('[data-price-wrapper]', scope) : null;

      const update = () => {
        const choices = [];
        $$('[data-variant-option]:checked', picker).forEach((input) => {
          choices[Number(input.dataset.optionIndex)] = input.value;
        });
        $$('[data-selected-option]', picker).forEach((element) => {
          element.textContent = choices[Number(element.dataset.selectedOption)] || '';
        });
        $$('[data-variant-option]', picker).forEach((input) => {
          const optionIndex = Number(input.dataset.optionIndex);
          const possible = variants.some((candidate) => candidate.available && candidate.options.every((value, index) => {
            if (index === optionIndex) return value === input.value;
            return !choices[index] || value === choices[index];
          }));
          input.disabled = !possible;
        });
        const variant = variants.find((candidate) => candidate.options.every((value, index) => value === choices[index]));
        if (!variant) {
          submit?.setAttribute('disabled', 'disabled');
          if (status) status.textContent = window.GoldCaftane.strings.soldOut;
          return;
        }
        if (selectedId) selectedId.value = variant.id;
        if (submit) {
          submit.disabled = !variant.available;
          submit.textContent = variant.available ? window.GoldCaftane.strings.addToBag : window.GoldCaftane.strings.soldOut;
        }
        if (status) status.textContent = variant.available ? '' : window.GoldCaftane.strings.soldOut;
        if (priceWrapper) {
          const current = $('[data-price-current]', priceWrapper);
          const compare = $('[data-price-compare]', priceWrapper);
          if (current) current.textContent = money(variant.price);
          if (variant.compare_at_price > variant.price) {
            if (compare) compare.textContent = money(variant.compare_at_price);
            else {
              const compareMarkup = document.createElement('s');
              compareMarkup.className = 'price__compare';
              compareMarkup.dataset.priceCompare = '';
              compareMarkup.textContent = money(variant.compare_at_price);
              priceWrapper.append(compareMarkup);
            }
            priceWrapper.classList.add('price--sale');
          } else {
            compare?.remove();
            priceWrapper.classList.remove('price--sale');
          }
        }
        const colorInput = $$('[data-variant-option]:checked', picker).find((input) => input.closest('.variant-picker__value--swatch'));
        const productSection = picker.closest('[data-product-section]');
        const gallery = productSection ? $('[data-product-gallery]', productSection) : null;
        gallery?.dispatchEvent(new CustomEvent('gold:filter-gallery', {
          detail: { color: colorInput?.value || '', mediaId: variant.featured_media?.id || variant.featured_image?.id || '' }
        }));
        form.dispatchEvent(new CustomEvent('gold:variant-change', { bubbles: true, detail: { variant } }));
      };

      picker.addEventListener('change', update);
      update();
    });
  }

  function initCommanderNow() {
    document.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-command-now]');
      if (!button) return;

      const productSection = button.closest('[data-product-section]') || document.querySelector(`[data-product-section-id="${button.closest('[data-product-section-id]')?.dataset.productSectionId || ''}"]`);
      if (!productSection) return;
      const productForm = $('.js-product-form', productSection);
      if (!productForm || productForm.dataset.commandNowSubmitting === 'true') return;

      event.preventDefault();
      const variantId = $('[data-selected-variant]', productForm)?.value;
      const quantity = Math.max(1, Number($('[name="quantity"]', productForm)?.value) || 1);
      const status = $('.product-form__status', productForm);
      if (!variantId) {
        if (status) status.textContent = 'Veuillez sélectionner une variante disponible.';
        return;
      }

      productForm.dataset.commandNowSubmitting = 'true';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (status) status.textContent = '';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || 'Impossible d’ajouter ce produit au panier.');
        window.location.assign('/checkout');
      } catch (error) {
        delete productForm.dataset.commandNowSubmitting;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (status) status.textContent = error.message || 'Une erreur est survenue. Réessayez.';
      }
    });
  }

  function initStickyPurchase() {
    if (!window.matchMedia('(max-width: 999px)').matches || !('IntersectionObserver' in window)) return;
    $$('[data-product-sticky-purchase]').forEach((sticky) => {
      const productSection = document.querySelector(`[data-product-section-id="${sticky.dataset.productSectionId}"]`);
      if (!productSection) return;
      const form = $('.js-product-form', productSection);
      if (!form) return;
      const addButton = $('[data-sticky-add]', sticky);
      addButton?.addEventListener('click', () => form.requestSubmit());
      const observer = new IntersectionObserver(([entry]) => { sticky.hidden = entry.isIntersecting; }, { threshold: .1 });
      observer.observe(form);
    });
  }

  function initCollectionControls() {
    const productGrid = $('[data-product-grid]');
    const controls = $$('[data-grid-density]');
    if (productGrid && controls.length) {
      const savedDensity = localStorage.getItem('gold-caftane-grid-density');
      if (savedDensity) setDensity(savedDensity);
      controls.forEach((button) => button.addEventListener('click', () => setDensity(button.dataset.gridDensity)));

      function setDensity(density) {
        productGrid.dataset.productGrid = density;
        controls.forEach((button) => button.classList.toggle('is-active', button.dataset.gridDensity === density));
        localStorage.setItem('gold-caftane-grid-density', density);
      }
    }

    const filters = $('[data-collection-filters]');
    $('[data-toggle-filters]')?.addEventListener('click', () => filters?.classList.add('is-open'));
    $('[data-close-filters]')?.addEventListener('click', () => filters?.classList.remove('is-open'));
  }

  function initPredictiveSearch() {
    const form = $('[data-predictive-search-form]');
    const input = $('[data-predictive-search-input]');
    const results = $('[data-predictive-search-results]');
    if (!form || !input || !results || !window.GoldCaftane?.predictiveSearchUrl) return;
    let controller;
    let timer;

    const render = (products) => {
      if (!products.length) {
        results.innerHTML = '<p class="eyebrow">No products found</p>';
        return;
      }
      results.innerHTML = products.map((product) => {
        const image = product.featured_image?.url || product.image || '';
        const imageMarkup = image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" width="130" height="154" loading="lazy">` : '';
        const price = product.price ? `<small>${escapeHtml(product.price)}</small>` : '';
        return `<a class="predictive-search-result" href="${escapeHtml(product.url)}">${imageMarkup}<span>${escapeHtml(product.title)}${price}</span></a>`;
      }).join('') + `<a class="text-button" href="${escapeHtml(form.action)}?q=${encodeURIComponent(input.value)}">View all results</a>`;
    };

    input.addEventListener('input', () => {
      const query = input.value.trim();
      window.clearTimeout(timer);
      if (query.length < 2) {
        results.innerHTML = '';
        return;
      }
      timer = window.setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        const url = new URL(window.GoldCaftane.predictiveSearchUrl, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '4');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        try {
          const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Predictive search request failed');
          const data = await response.json();
          render(data.resources?.results?.products || []);
        } catch (error) {
          if (error.name !== 'AbortError') results.innerHTML = '';
        }
      }, 180);
    });
  }

  function initCart() {
    const drawer = $('[data-cart-drawer]');
    if (!drawer || !window.GoldCaftane) return;
    const panel = $('.cart-drawer__panel', drawer);
    const body = $('[data-cart-body]', drawer);
    let lastCartTrigger = null;

    const setOpen = (open, trigger) => {
      if (open) {
        lastCartTrigger = trigger || document.activeElement;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        window.setTimeout(() => $(focusableSelector, panel)?.focus(), 20);
      } else {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        if (lastCartTrigger instanceof HTMLElement) lastCartTrigger.focus();
      }
    };

    const updateCount = (count) => $$('[data-cart-count]').forEach((node) => { node.textContent = count; });
    const lineMarkup = (item) => {
      const image = item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" width="184" height="224" loading="lazy">` : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<small>${escapeHtml(item.variant_title)}</small>` : '';
      return `<li class="cart-line" data-cart-line="${escapeHtml(item.key)}">
        <a class="cart-line__image" href="${escapeHtml(item.url)}">${image}</a>
        <div class="cart-line__details"><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>${variant}<span>${money(item.final_line_price)}</span>
          <div class="cart-line__actions"><input aria-label="Quantity" type="number" value="${item.quantity}" min="0" data-cart-quantity="${escapeHtml(item.key)}"><button type="button" class="text-button" data-cart-remove="${escapeHtml(item.key)}">${escapeHtml(window.GoldCaftane.strings.remove)}</button></div>
        </div>
      </li>`;
    };

    const render = (cart) => {
      updateCount(cart.item_count);
      if (!cart.item_count) {
        body.innerHTML = `<div class="cart-drawer__empty" data-cart-empty><h2>${escapeHtml(window.GoldCaftane.strings.emptyCart)}</h2><p>Découvrez nos collections :</p><nav class="cart-catalog-links" aria-label="Collections Gold Caftane"><a href="/collections/caftane">Caftane</a><a href="/collections/takshita">Takshita</a><a href="/collections/djellaba-deux-pieces">Djellaba deux pièces</a><a href="/collections/djellaba">Djellaba</a></nav></div>`;
        return;
      }
      body.innerHTML = `<form action="${escapeHtml(window.GoldCaftane.cartUrl)}" method="post" class="cart-drawer__form"><ul class="cart-lines" data-cart-lines>${cart.items.map(lineMarkup).join('')}</ul><footer class="cart-drawer__footer"><div class="cart-drawer__subtotal"><span>Sous-total</span><strong data-cart-total>${money(cart.total_price)}</strong></div><button class="button cart-checkout-button" type="submit" name="checkout">Yallah, validez !</button></footer></form>`;
    };

    const getCart = async () => {
      const response = await fetch(window.GoldCaftane.cartJsonUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Could not refresh cart');
      const cart = await response.json();
      render(cart);
      return cart;
    };

    const changeLine = async (key, quantity) => {
      const response = await fetch(window.GoldCaftane.cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error('Could not update cart');
      render(await response.json());
    };

    $$('[data-open-cart]').forEach((button) => button.addEventListener('click', async () => {
      setOpen(true, button);
      try { await getCart(); } catch (error) { /* Existing server markup remains usable. */ }
    }));
    $$('[data-close-drawer]', drawer).forEach((button) => button.addEventListener('click', () => setOpen(false)));
    panel.addEventListener('keydown', (event) => trapFocus(event, panel));
    drawer.addEventListener('keydown', (event) => { if (event.key === 'Escape') setOpen(false); });

    body.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      changeLine(remove.dataset.cartRemove, 0).catch(() => {});
    });
    body.addEventListener('change', (event) => {
      const quantity = event.target.closest('[data-cart-quantity]');
      if (!quantity) return;
      changeLine(quantity.dataset.cartQuantity, Math.max(0, Number(quantity.value) || 0)).catch(() => {});
    });

    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('form.js-product-form');
      if (!form) return;
      event.preventDefault();
      const status = $('.product-form__status', form);
      const submit = $('.product-form__submit', form);
      submit?.setAttribute('aria-busy', 'true');
      if (status) status.textContent = '';
      try {
        const response = await fetch(window.GoldCaftane.cartAddUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.description || 'Could not add item to bag');
        }
        await getCart();
        setOpen(true, submit);
      } catch (error) {
        if (status) status.textContent = error.message;
      } finally {
        submit?.removeAttribute('aria-busy');
      }
    });
  }

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-open-dialog]');
    if (openButton) {
      event.preventDefault();
      openDialog(openButton.dataset.openDialog, openButton);
      return;
    }
    const closeButton = event.target.closest('[data-close-dialog]');
    if (closeButton) closeDialog(closeButton.closest('dialog'));
  });

  $$('dialog').forEach((dialog) => {
    dialog.addEventListener('click', (event) => {
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeDialog(dialog);
    });
    dialog.addEventListener('keydown', (event) => trapFocus(event, dialog));
  });

  $$('[data-scroll-to]').forEach((button) => button.addEventListener('click', () => document.getElementById(button.dataset.scrollTo)?.scrollIntoView({ behavior: 'smooth' })));

  initAnnouncement();
  initHeader();
  initServiceSlider();
  initLocalization();
  initGallery();
  initCommanderNow();
  initVariantPickers();
  initStickyPurchase();
  initCollectionControls();
  initPredictiveSearch();
  initCart();
})();
